/** One bundled package from Angular's extracted `3rdpartylicenses.txt`. */
export interface ThirdPartyLicense {
  name: string;
  /** The package.json `license` identifier; `null` when missing or `SEE LICENSE IN …`. */
  license: string | null;
  /** The package's license file, or empty when it ships none. */
  text: string;
}

// The file lists each package as `Package: <name>`, `License: <JSON>`, a blank
// line and the license text, closed by 80 dashes. Only split where the next
// entry (or the end) follows, so dashed lines inside a license text survive.
const ENTRY_SEPARATOR = /\n-{80}\n(?=Package: |$)/;
const PACKAGE_PREFIX = 'Package: ';
const LICENSE_PREFIX = 'License: ';

/**
 * Parses Angular's `extractLicenses` output. Anything else (a 404 body or the
 * dev server's HTML fallback) yields no entries.
 */
export function parseThirdPartyLicenses(content: string): ThirdPartyLicense[] {
  const seen = new Set<string>();
  const entries: ThirdPartyLicense[] = [];
  for (const chunk of content.replace(/\r\n/g, '\n').split(ENTRY_SEPARATOR)) {
    const block = chunk.replace(/^\n+/, '');
    if (!block.startsWith(PACKAGE_PREFIX)) continue;
    const nameEnd = block.indexOf('\n');
    const name = block.slice(PACKAGE_PREFIX.length, nameEnd < 0 ? undefined : nameEnd).trim();
    const rest = nameEnd < 0 ? '' : block.slice(nameEnd + 1);
    if (!name || !rest.startsWith(LICENSE_PREFIX)) continue;
    // Pretty-printed JSON has no blank lines, so the first one ends the field.
    const fieldEnd = rest.indexOf('\n\n');
    const field = rest.slice(LICENSE_PREFIX.length, fieldEnd < 0 ? undefined : fieldEnd);
    const entry: ThirdPartyLicense = {
      name,
      license: licenseIdentifier(field),
      text: fieldEnd < 0 ? '' : rest.slice(fieldEnd + 2).trim(),
    };
    // Several versions of one package often carry the same license.
    const key = JSON.stringify(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push(entry);
  }
  return entries.sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

function licenseIdentifier(field: string): string | null {
  let value: unknown;
  try {
    value = JSON.parse(field);
  } catch {
    return null; // `undefined` when the package declares no license
  }
  const identifiers = (Array.isArray(value) ? value : [value])
    .map((item: unknown) =>
      typeof item === 'object' && item !== null ? (item as { type?: unknown }).type : item
    )
    .filter(
      (item): item is string =>
        typeof item === 'string' && !!item.trim() && !/^SEE LICENSE IN /i.test(item)
    );
  return identifiers.length ? identifiers.join(' OR ') : null;
}
